import React, { useCallback, useEffect, useState } from "react";
// Shared so the two panels cannot disagree about what an attainment figure means.
import { renderAttainment } from "./InitiativeImpactPanel";
import {
  getInitiativePerformance,
  initiativeErrorMessage,
} from "../services/efficiencyInitiativesApi";
import FieldHelp from "../help/FieldHelp";

/**
 * Initiative Performance — rankings and outliers.
 *
 * Three presentation rules, each of which exists to stop a misleading reading:
 *
 *   * Initiatives whose windows lack data coverage are listed separately, never
 *     ranked. Ranking them as zero would place a project with no measurements
 *     below one that genuinely made things worse.
 *   * "No expectation set" is kept apart from "underperformed". Not being able to
 *     judge an initiative is not the same as judging it badly.
 *   * Ranking by loss reduction is real, but it is not free: it makes the server
 *     run the insight-loss comparison for every initiative, which can exceed its
 *     budget and measure only part of the portfolio. When that happens the order
 *     is withheld rather than drawn from a mostly-empty column.
 */

// `col` marks which table column each ranking drives, so the active one can be
// highlighted. Without that, switching rankings looks like it does nothing
// whenever the resulting order happens to be unchanged — which is common, since
// financial savings are just energy x tariff and therefore almost always rank the
// same way.
const RANK_OPTIONS = [
  { key: "energy", label: "Energy savings", col: "energy", dir: "highest first" },
  { key: "financial", label: "Financial savings", col: "financial", dir: "highest first" },
  { key: "payback", label: "Payback period", col: "payback", dir: "shortest first" },
  { key: "loss", label: "Loss reduction", col: "loss", dir: "highest first" },
];

// Ranking on loss forces the server's insight-loss pass on, which is the slow
// path. Kept as a set rather than a string compare so adding a second
// loss-derived ranking later does not need this logic changed in two places.
const INSIGHT_LOSS_RANKINGS = new Set(["loss"]);

const PKR = new Intl.NumberFormat("en-PK", { maximumFractionDigits: 0 });
const NUM = new Intl.NumberFormat("en-PK", { maximumFractionDigits: 1 });

const fmtKwh = (v) => (v == null ? "—" : PKR.format(v));
const fmtPkr = (v) => (v == null ? "—" : PKR.format(v));
const fmtPayback = (m) =>
  m == null ? "no payback" : m < 24 ? `${NUM.format(m)} mo` : `${NUM.format(m / 12)} yr`;

export default function InitiativePerformancePanel({ username, statusFilter }) {
  const [rankBy, setRankBy] = useState("energy");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!username) return;
    setLoading(true);
    setError(null);
    try {
      setData(
        await getInitiativePerformance(username, {
          rankBy,
          status: statusFilter,
          top: 3,
          includeInsightLoss: INSIGHT_LOSS_RANKINGS.has(rankBy),
        })
      );
    } catch (err) {
      setError(initiativeErrorMessage(err, "Could not rank initiatives."));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [username, rankBy, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const topRefs = new Set((data?.top_performers || []).map((t) => t.reference_code));
  const underRefs = new Set((data?.underperformers || []).map((u) => u.reference_code));
  const activeOpt = RANK_OPTIONS.find((o) => o.key === rankBy);
  // The loss column is shown only while it is the active ranking; see the header.
  const showLoss = INSIGHT_LOSS_RANKINGS.has(rankBy);
  const activeCol = activeOpt?.col || null;

  return (
    <div className="ei-card">
      <div className="ei-impact-head">
        <h4>
          <FieldHelp id="efficiencyInitiatives.performanceTitle">Initiative performance</FieldHelp>{" "}
          <span className="ei-impact-sub">
            measured savings, ranked · cancelled initiatives excluded
          </span>
        </h4>
      </div>

      {activeOpt?.col ? (
        <p className="ei-rank-note">
          Ordered by <strong>{activeOpt.label.toLowerCase()}</strong> ({activeOpt.dir}) —
          click any highlighted heading to re-rank.
          {/* The only remaining way in, now that the column is hidden by default.
              Without it, ranking by loss reduction would be unreachable from the
              UI even though the server still supports it. */}
          {!showLoss && (
            <>
              {" "}
              <button
                type="button"
                className="ei-link-btn"
                onClick={() => setRankBy("loss")}
                title="Runs the insight detectors over both periods, so it is slower."
              >
                Rank by loss reduction
              </button>
            </>
          )}
        </p>
      ) : (
        <p className="ei-rank-note">
          Click a column heading to rank by it.
        </p>
      )}

      {error && <div className="ei-alert error">{error}</div>}
      {loading && (
        <p className="ei-hint">
          Computing measured impact for every initiative…
          {INSIGHT_LOSS_RANKINGS.has(rankBy) && " Ranking by loss takes longer."}
        </p>
      )}

      {!loading && data && (
        <>
          {!data.ranking_available && (
            <div className="ei-alert info" title={data.ranking_unavailable_reason}>
              Cannot rank by {activeOpt?.label.toLowerCase() || rankBy} — default
              order shown.
            </div>
          )}

          {/* The two caps are reported separately because they mean different
              things: truncated is "we did not look at these", timed out is "we
              looked and ran out of time". Both leave the loss column blank, and
              neither is the initiative's fault. */}
          {data.insight_loss_timed_out > 0 && (
            <div
              className="ei-alert info"
              title="Blank means not measured, not zero. Open an initiative to measure it."
            >
              {data.insight_loss_timed_out} initiative
              {data.insight_loss_timed_out === 1 ? "" : "s"} timed out — loss blank.
            </div>
          )}

          {data.insight_loss_truncated > 0 && (
            <div className="ei-alert info" title="Skipped to keep the request responsive.">
              {data.insight_loss_truncated} initiative
              {data.insight_loss_truncated === 1 ? "" : "s"} skipped — loss blank.
            </div>
          )}

          {data.truncated && (
            <div className="ei-alert info">
              Only the {data.count} most recent initiatives were ranked; older ones were
              excluded to keep this responsive.
            </div>
          )}

          {data.count === 0 ? (
            <div className="ei-empty">
              <strong>Nothing to rank</strong>
              No initiative yet has enough meter data on both sides of its
              implementation date.
            </div>
          ) : (
            <div className="ei-table-wrapper">
              <table className="ei-table">
                <thead>
                  <tr>
                    <th>
                      {data.ranking_available ? (
                        <FieldHelp id="efficiencyInitiatives.rank">#</FieldHelp>
                      ) : (
                        ""
                      )}
                    </th>
                    <th><FieldHelp id="efficiencyInitiatives.referenceCode">ID</FieldHelp></th>
                    <th><FieldHelp id="efficiencyInitiatives.initiative">Initiative</FieldHelp></th>
                    <th><FieldHelp id="efficiencyInitiatives.node">Node</FieldHelp></th>
                    {/* The headings ARE the ranking control — clicking one re-ranks. */}
                    <SortableTh
                      label="Energy (kWh/yr)" rankKey="energy" helpId="perfEnergy"
                      rankBy={rankBy} onSort={setRankBy} arrow="↓"
                    />
                    <SortableTh
                      label="Financial (PKR/yr)" rankKey="financial" helpId="perfFinancial"
                      rankBy={rankBy} onSort={setRankBy} arrow="↓"
                    />
                    <SortableTh
                      label="Payback" rankKey="payback" helpId="perfPayback"
                      rankBy={rankBy} onSort={setRankBy} arrow="↑"
                    />
                    {/* Only when it IS the ranking. Left permanently visible it
                        read "not measured" on every row, because the insight-loss
                        pass is opt-in and nothing requests it otherwise — a column
                        of identical placeholders. Ranking by it turns the pass on,
                        and then the figures are the point of the column. */}
                    {showLoss && (
                      <SortableTh
                        label="Loss reduction (PKR/yr)" rankKey="loss" helpId="perfLoss"
                        rankBy={rankBy} onSort={setRankBy} arrow="↓"
                      />
                    )}
                    <th className="ei-num"><FieldHelp id="efficiencyInitiatives.attainment">vs expected</FieldHelp></th>
                    <th><FieldHelp id="efficiencyInitiatives.status">Status</FieldHelp></th>
                  </tr>
                </thead>
                <tbody>
                  {data.ranked.map((e) => {
                    const attain = e.expected_vs_actual?.kwh_attainment_percent;
                    const isTop = topRefs.has(e.reference_code);
                    const isUnder = underRefs.has(e.reference_code);
                    return (
                      <tr key={e.initiative_id} className="ei-row">
                        <td className="ei-rank">
                          {e.rank != null ? e.rank : "—"}
                          {isTop && <span className="ei-star" title="Top performer">★</span>}
                        </td>
                        <td className="ei-ref">{e.reference_code}</td>
                        <td>
                          <div className="ei-title-cell">{e.title}</div>
                        </td>
                        <td>
                          <span className={`ei-badge node-${e.node_type}`}>{e.node_type}</span>
                          <div className="ei-title-sub">{e.node_name}</div>
                        </td>
                        <td className={`ei-num${activeCol === "energy" ? " ei-col-active" : ""}`}>
                          {fmtKwh(e.energy?.annualised_saving_kwh)}
                        </td>
                        <td className={`ei-num${activeCol === "financial" ? " ei-col-active" : ""}`}>
                          {fmtPkr(e.financial?.annualised_saving_pkr)}
                        </td>
                        <td className={`ei-num${activeCol === "payback" ? " ei-col-active" : ""}`}>
                          {fmtPayback(e.financial?.simple_payback_months)}
                        </td>
                        {/* loss_reduction is an object, not a number: it carries
                            its own availability and reason so a blank cell can be
                            explained instead of just being blank. */}
                        {showLoss && (
                          <td className={`ei-num${activeCol === "loss" ? " ei-col-active" : ""}`}>
                            {e.loss_reduction?.available ? (
                              <strong
                                className={
                                  e.loss_reduction.annualised_pkr > 0 ? "ei-good" : "ei-bad"
                                }
                              >
                                {fmtPkr(e.loss_reduction.annualised_pkr)}
                              </strong>
                            ) : (
                              <span className="ei-hint" title={e.loss_reduction?.reason}>
                                not measured
                              </span>
                            )}
                          </td>
                        )}
                        <td className="ei-num">
                          {/* Shared with the impact panel so one initiative cannot
                              read "worse than baseline" there and "-2,054.1%" here.
                              The old branch keyed the colour off isUnder rather than
                              the number, so a regression and a near-miss were the
                              same red. */}
                          {renderAttainment(
                            attain,
                            e.energy?.annualised_saving_kwh,
                            e.expected_vs_actual?.expected_annual_kwh
                          )}
                        </td>
                        <td>
                          <span className={`ei-badge status-${e.status}`}>{e.status}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="ei-impact-cols" style={{ marginTop: "1.5rem" }}>
            <div>
              {/* Overall, NOT "by whichever column is selected". A leaderboard whose
                  meaning changes every time a heading is clicked is not a
                  leaderboard. This list is stable across the ranking toggle. */}
              <p className="ei-detail-label"><FieldHelp id="efficiencyInitiatives.overallTop">Highest performing overall</FieldHelp></p>
              {/* One clause, because an initiative appearing in both lists reads as
                  a contradiction otherwise. */}
              <p className="ei-hint" style={{ margin: "0 0 0.5rem" }}>
                Against each other, not against their targets.
              </p>
              {data.overall_top_performers?.length ? (
                <>
                  <ol className="ei-rank-list">
                    {data.overall_top_performers.map((t) => (
                      <li key={t.initiative_id}>
                        <strong>{t.reference_code}</strong> {t.title}
                        <div className="ei-title-sub">
                          {fmtKwh(t.energy?.annualised_saving_kwh)} kWh/yr ·{" "}
                          {fmtPkr(t.financial?.annualised_saving_pkr)} PKR/yr ·{" "}
                          {fmtPayback(t.financial?.simple_payback_months)}
                        </div>
                        {/* Rendered from the object's own keys, not a fixed list:
                            the server adds a "loss" dimension when every entry
                            has a measurable figure, and drops it when they do
                            not, so hardcoding three would either omit it or
                            print "undefined". */}
                        {t.overall_positions && (
                          <div className="ei-title-sub">
                            avg rank {t.overall_average_rank} —{" "}
                            {Object.entries(t.overall_positions)
                              .map(([dim, pos]) => `${dim} #${pos}`)
                              .join(", ")}
                          </div>
                        )}
                        {underRefs.has(t.reference_code) && (
                          <div className="ei-title-sub ei-bad">
                            …but below its own target (
                            {NUM.format(
                              t.expected_vs_actual?.kwh_attainment_percent
                            )}
                            % of {fmtKwh(t.expected_vs_actual?.expected_annual_kwh)} kWh/yr)
                          </div>
                        )}
                      </li>
                    ))}
                  </ol>
                  <p
                    className="ei-hint"
                    style={{ marginTop: "0.5rem" }}
                    title={`${data.overall_method}. ${data.overall_caveat}.`}
                  >
                    {data.overall_top_performers.length >= data.count
                      ? `All ${data.count} measurable — an ordering, not a shortlist.`
                      : "Average rank across every measured dimension."}
                  </p>
                </>
              ) : (
                <p className="ei-hint">Nothing measurable to rank yet.</p>
              )}
            </div>

            <div>
              <p className="ei-detail-label">
                <FieldHelp id="efficiencyInitiatives.underperformers">Below expected performance</FieldHelp> (under{" "}
                {data.underperform_threshold_percent}% of their own target)
              </p>
              {data.underperformers.length === 0 ? (
                <p className="ei-hint">
                  None — every measurable initiative is meeting or beating its expected
                  saving.
                </p>
              ) : (
                <ul className="ei-rank-list">
                  {data.underperformers.map((u) => (
                    <li key={u.initiative_id}>
                      <strong>{u.reference_code}</strong> {u.title}
                      <div className="ei-title-sub ei-bad">
                        {/* Lead with the direction, not the ratio: a negative actual
                            makes "% of expected" a number that cannot be read. */}
                        {(u.energy?.annualised_saving_kwh ?? 0) < 0
                          ? `worse than baseline — ${fmtKwh(
                              u.energy?.annualised_saving_kwh
                            )} kWh/yr against a target of ${fmtKwh(
                              u.expected_vs_actual.expected_annual_kwh
                            )}`
                          : `${NUM.format(
                              u.expected_vs_actual.kwh_attainment_percent
                            )}% of expected — ${fmtKwh(
                              u.energy?.annualised_saving_kwh
                            )} kWh/yr against a target of ${fmtKwh(
                              u.expected_vs_actual.expected_annual_kwh
                            )}`}
                      </div>
                      {/* Confidence belongs here more than anywhere else in this
                          panel: calling an initiative underperforming on a figure
                          the server itself rates as low-confidence sends someone to
                          argue with a plant manager about noise. */}
                      {u.confidence?.level && u.confidence.level !== "high" && (
                        <div
                          className={`ei-conf-inline ${u.confidence.level}`}
                          title={u.confidence.limiting_factor || undefined}
                        >
                          {u.confidence.level === "unavailable"
                            ? "impact not measurable"
                            : `${u.confidence.level} confidence`}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {data.no_expectation_set.length > 0 && (
                <p className="ei-hint" style={{ marginTop: "0.6rem" }}>
                  Cannot be judged (no expected saving recorded):{" "}
                  {data.no_expectation_set.join(", ")}
                </p>
              )}
            </div>
          </div>

          {data.not_measurable.length > 0 && (
            <div style={{ marginTop: "1.25rem" }}>
              <p className="ei-detail-label">Excluded — <FieldHelp id="efficiencyInitiatives.notMeasurable">not yet measurable</FieldHelp></p>
              <ul className="ei-rank-list">
                {data.not_measurable.map((n) => (
                  <li key={n.initiative_id}>
                    <strong>{n.reference_code}</strong> {n.title}
                    <div className="ei-title-sub">{n.reason}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}


/**
 * A column heading that doubles as the ranking control.
 *
 * Kept as a real <button> inside the <th> rather than an onClick on the th, so it
 * is reachable by keyboard and announced as actionable — a heading that silently
 * responds to clicks is invisible to anyone not using a mouse.
 */
function SortableTh({ label, rankKey, rankBy, onSort, arrow, title, helpId }) {
  const active = rankBy === rankKey;
  return (
    <th className={`ei-num ei-sortable${active ? " ei-col-active" : ""}`} title={title}>
      <button
        type="button"
        className="ei-sort-btn"
        onClick={() => onSort(rankKey)}
        aria-pressed={active}
      >
        {helpId ? (
          <FieldHelp id={`efficiencyInitiatives.${helpId}`}>{label}</FieldHelp>
        ) : (
          label
        )}
        <span className="ei-sort-arrow">{active ? arrow : ""}</span>
      </button>
    </th>
  );
}
